import { useStore } from '@/hooks/useStore';
import { cn } from '@/utils/utils';
import { formatCurrency } from '@/utils/formatNumber';
import { observer } from 'mobx-react-lite';
import React, { cloneElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import yup from '@/services/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { DATE_SUBMIT_YMD, DATE_TIME_ISO_NO_TIMEZONE, DISPPLAY_MOMENT_YMD_HM_FULLWIDTH, ekDomain, ROUTES, VALID_DURATION_BOOKING } from '@/configs/constants';
import { ReactDayPickerRange } from '@/components/calendar/ReactDayPickerRange';
import { isPathEk } from '@/utils/routes';
import { v4 as uuidv4 } from 'uuid';
import { getFromSession, saveToSession } from '@/utils/browsers';
import { flowResult } from 'mobx';

import './styles.scss';
import { addMonths, isBefore, subDays, isAfter, isSameDay } from 'date-fns';
import { NavProps } from 'react-day-picker';
import { formatDateTimeISOJST, formatDisplayDateTime, formatSpecificDateTimeISOJST, formatTotalTime } from '@/utils/datetime';
import { COMMON_TYPE } from '@/types/enums';
import moment from 'moment';
import StepWrapper from '../components/step-container/StepWrapper';
import CustomTimePickerVer2 from '@/components/calendar/CustomTimePickerVer2';
import AnimateButton from '@/components/general/button-custom/AnimateButton';
import { XCusIcon } from '@/components/icon/XCusIcon';

export default observer(function StepFirstBookingPage() {
    // hook
    const { t } = useTranslation();
    const { id, bikeId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const isEk = isPathEk(pathname);

    const type = isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS;

    // store
    const {
        bookingStore: {
            getBikeBookingDetail,
            postCheckingBookingData, modifiers,
            setObservable
        }, modalStore: { showAlertModal }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        // date
        selectDate: yup
            .mixed()
            .required('form_error_validate.required_select_rental_date'),
        startTime: yup
            .string()
            .trim()
            .required('form_error_validate.required'),
        endTime: yup
            .string()
            .trim()
            .required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            selectDate: undefined,
            startTime: '',
            endTime: ''
        }
    });

    const watchSelectedDates: any = form.watch('selectDate');
    const watchStartTime = form.watch('startTime');
    const watchEndTime = form.watch('endTime');

    // const
    const nowInPlus9 = moment().utcOffset(9 * 60);
    const limitMonth = addMonths(new Date(), 6); // Tính tháng giới hạn (hiện tại + 6 tháng)

    const [fromMonth, setFromMonth] = useState(new Date());
    // const [currentMonth, setCurrentMonth] = useState(new Date());
    const [earliestMonth, setEarliestmonth] = useState(addMonths(new Date(), -1).getMonth());
    const [disableDates, setDisableDates] = useState([
        { before: new Date() },
        { after: limitMonth }
    ]);
    const [blockedDates, setBlockDates] = useState<Date[]>([]);
    const [totalTime, setTotalTime] = useState<string>('');
    const [totalAmount, setTotalAmount] = useState<number>(0);

    const toMonth = addMonths(fromMonth, 1);

    // const

    // lifecycle
    useEffect(() => {
        const uuid = searchParams.get('uuid');
        if (!uuid) {
            const sessionToken = uuidv4();
            const newParams = new URLSearchParams();
            newParams.set('uuid', sessionToken);
            setSearchParams(newParams);
        } else {
            const currentData = getFromSession(uuid);
            if (currentData) {
                form.reset({
                    selectDate: {
                        from: moment.parseZone(currentData.startTime).format(DATE_SUBMIT_YMD),
                        to: moment.parseZone(currentData.endTime).format(DATE_SUBMIT_YMD)
                    },
                    startTime: moment.parseZone(currentData.startTime).format('HH:mm'),
                    endTime: moment.parseZone(currentData.endTime).format('HH:mm')
                });
                setFromMonth(new Date(currentData.startTime));
                // setCurrentMonth(new Date(currentData.startTime));
            }
        }
    }, []);
    useEffect(() => {
        getBikeBookingData(Number(bikeId), Number(id));

        return () => {
            // setObservable('bikeBookingDetail', undefined);
        };
    }, [searchParams]);

    useEffect(() => {
        if (watchSelectedDates?.from && watchSelectedDates?.to && watchStartTime && watchEndTime) {
            const payload = {
                locationId: id,
                bikeId: bikeId,
                startTime: formatSpecificDateTimeISOJST(watchSelectedDates?.from, watchStartTime),
                endTime: formatSpecificDateTimeISOJST(watchSelectedDates?.to, watchEndTime),
                isValidateDate: false
            };
            handleCheckData(payload);
        } else {
            setTotalTime('');
            setTotalAmount(0);
        }
    }, [watchSelectedDates, watchStartTime, watchEndTime]);

    // function
    const handleCheckData = async (payload) => {
        const res = await flowResult(postCheckingBookingData(payload, true));
        if (res) {
            setTotalTime(formatTotalTime(res.dateAndTime, t));
            setTotalAmount(res.dateAndTimeCost || 0);
        }
    };

    // Handler khi chuyển tháng bên phải
    const handleNextMonth = () => {
        setFromMonth(prev => addMonths(prev, 1));
    };

    const isInBookedRange = (date: Date, range: { from: Date, to: Date }) => {
        return (isAfter(date, range?.from) && isBefore(date, range?.to)) || isSameDay(date, range?.from) || isSameDay(date, range?.to);
    };

    const getBikeBookingData = async (bikeId: number, locationId: number) => {
        const res = await flowResult(getBikeBookingDetail(bikeId, locationId, type));
        if (res) {
            let datesWithStatus = [] as any;
            let disableDates = [] as any[];

            let dayPickerModifiers: Record<string, Date[]> = {} as Record<string, Date[]>;
            if (res.nonRentalDatesDetails && res?.nonRentalDatesDetails.length > 0) {
                res?.nonRentalDatesDetails.forEach((data) => {
                    datesWithStatus.push({
                        date: new Date(data),
                        status: 'blocked'
                    });
                });
            }

            if (res.timeHasBeenBooking && res.timeHasBeenBooking.length > 0) {
                res.timeHasBeenBooking.forEach((data) => {
                    const rentalFromJST = moment.utc(data.rentalFrom).add(9, 'hours').format(DATE_TIME_ISO_NO_TIMEZONE);
                    const rentalToJST = moment.utc(data.rentalTo).add(9, 'hours').format(DATE_TIME_ISO_NO_TIMEZONE);

                    datesWithStatus.push({
                        range: {
                            from: new Date(rentalFromJST),
                            to: new Date(rentalToJST)
                        },
                        status: 'booked'
                    });
                });
            }

            if (datesWithStatus.length > 0) {
                dayPickerModifiers = {
                    ...dayPickerModifiers, ...datesWithStatus.reduce(
                        (acc, { date, status, range }) => {
                            if (status === 'blocked') {
                                acc[status] = acc[status] || [];
                                acc[status].push(date);
                                acc.disabled = [...(acc.disabled || []), date];
                            } else if (status === 'booked' && range) {
                                acc[status] = acc[status] || [];
                                acc[status].push(range); // Lưu range vào mảng booked
                            }
                            return acc;
                        },
                        { booked: [], blocked: [], disabled: [] } as Record<string, Date[]>
                    )
                };
                disableDates.push(...[...datesWithStatus.filter(date => date.status === 'blocked').map(item => ({ date: item.date }))]);
                // setDisableDates(prev => [...prev, ...datesWithStatus.filter(date => date.status === 'blocked').map(item => ({ date: item.date }))]);
                setBlockDates([...datesWithStatus.map(item => item.date)]);
            }

            if (res.bookingLeadTime && !Number.isNaN(Number(res.bookingLeadTime))) {
                const earliest = nowInPlus9
                    .clone()
                    .add(Number(res.bookingLeadTime), 'hours');
                // .startOf('day');

                if (earliest.minutes() > 0) {
                    earliest.add(1, 'hour').startOf('hour');
                } else {
                    earliest.startOf('hour');
                }

                const year = earliest.year(); // ví dụ 2025
                const month = earliest.month(); // 0–11, tức 0 = Jan
                const day = earliest.date(); // 1–31

                // 3. Tạo Date local 00:00 cho calendar
                const onlyDate = new Date(year, month, day);

                disableDates.push(...[{ before: onlyDate }, { after: addMonths(new Date(), 6) }]);
                setEarliestmonth(onlyDate.getMonth());
            }

            if (disableDates.length > 0) {
                setDisableDates(prev => [...prev, ...disableDates]);
            }

            setObservable('modifiers', dayPickerModifiers);
        }
    };

    const handleSelectDate = (range: { from?: Date, to?: Date } | undefined) => {
        if (!range || !range?.from || !range?.to) {
            form.reset({
                ...form.getValues(),
                selectDate: undefined
            });
            return;
        }

        const { from, to } = range;

        // Kiểm tra xem có ngày bị blocked trong phạm vi chọn hay không
        let validTo = to;
        for (const blocked of blockedDates) {
            if (isBefore(blocked, to) && !isBefore(blocked, from)) {
                validTo = subDays(blocked, 1); // Giới hạn phạm vi trước ngày blocked
                break;
            }
        }

        // Cập nhật phạm vi hợp lệ
        form.setValue('selectDate', { from, to: validTo });
        // validTo && setCurrentMonth(validTo);
        form.trigger('selectDate');
    };

    const generateModifiers = (date: Date) => {
        let status = '';

        // Kiểm tra nếu ngày nằm trong phạm vi của blocked
        const { blocked, booked } = modifiers;
        if (blocked?.some(d => isSameDay(d, date))) {
            status = 'blocked';
        }

        // Kiểm tra nếu ngày nằm trong phạm vi của booked
        if (booked?.some(range => isInBookedRange(date, range))) {
            status = 'booked';
        }

        return status;
    };

    const onSubmit = async () => {
        let startTime = formatDisplayDateTime(watchSelectedDates?.from, watchStartTime);
        let endTime = formatDisplayDateTime(watchSelectedDates?.to, watchEndTime);

        let startSubmitTime = formatSpecificDateTimeISOJST(watchSelectedDates?.from, watchStartTime);
        let endTSubmitime = formatSpecificDateTimeISOJST(watchSelectedDates?.to, watchEndTime);

        let timeLocked = formatDateTimeISOJST(new Date());
        let expireTimeLocked = formatDateTimeISOJST(moment().add(VALID_DURATION_BOOKING, 'minutes').toDate());

        if (endTime && startTime) {
            const rentalFrom = new Date(startTime);
            const rentalTo = new Date(endTime);

            if (rentalTo.getTime() <= rentalFrom.getTime()) {
                showAlertModal({
                    id: 'time-error',
                    type: 'error',
                    content: t('server_messages.ERROR_RENTAL_FROM_TO_TIME_INVALID')
                });
            } else {
                const sessionToken = searchParams.get('uuid');
                const data = getFromSession(sessionToken || '') || {};
                const payload = {
                    ...data,
                    locationId: id,
                    bikeId: bikeId,
                    startTime: startSubmitTime,
                    endTime: endTSubmitime,
                    timeLocked, expireTimeLocked
                };

                const res = await flowResult(postCheckingBookingData(payload));
                if (res) {
                    // const sessionToken = searchParams.get('uuid');
                    if (sessionToken) {
                        saveToSession(sessionToken, {
                            ...payload,
                            dateAndTimeCost: res.dateAndTimeCost || 0,
                            dateAndTime: res.dateAndTime,
                            timeLocked, expireTimeLocked
                        });
                        navigate(
                            isEk ?
                                `/${ekDomain}/${ROUTES.locationList.href}/detail/${id}/bike-booking-second/${bikeId}?uuid=${sessionToken}` :
                                `/${ROUTES.locationList.href}/detail/${id}/bike-booking-second/${bikeId}?uuid=${sessionToken}`
                        );
                    }
                }
            }
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='relative'>
                <StepWrapper currentStep={1}>
                    {/* Choose date time */}
                    <div className='md:mt-20 xsm:mt-[60px] mt-[30px]'>
                        <div>
                            <div className='mb-6'>
                                <FormField
                                    control={form.control}
                                    name='selectDate'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div className='w-full lg:grid lg:grid-cols-2 gap-5'>
                                                    <div className='lg:block hidden'>
                                                        <ReactDayPickerRange
                                                            mode='range'
                                                            className='max-w-full'
                                                            showOutsideDays={false}
                                                            fixedWeeks
                                                            selected={field.value as any}
                                                            onSelect={(date) => {
                                                                handleSelectDate(date);
                                                            }}
                                                            month={fromMonth}
                                                            onMonthChange={setFromMonth}
                                                            modifiers={{
                                                                booked: (date: Date) => generateModifiers(date) === 'booked',
                                                                blocked: (date: Date) => generateModifiers(date) === 'blocked'
                                                            }}
                                                            modifiersClassNames={{
                                                                booked: 'day-booked',
                                                                blocked: 'day-blocked'
                                                            }}
                                                            disabled={disableDates}
                                                            captionLayout='label'
                                                            components={{
                                                                Nav: ({ children, className, ...props }: NavProps) => {
                                                                    const [previousButton] = React.Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;
                                                                    const prevWithClass = React.cloneElement(previousButton, {
                                                                        disabled: fromMonth.getMonth() <= earliestMonth && fromMonth.getFullYear() === (new Date()).getFullYear()
                                                                        // nếu cần override hoàn toàn className, thay bằng:
                                                                        // className: 'my-prev-class'
                                                                    });

                                                                    return (
                                                                        <nav className={cn('flex items-center min-h-8 justify-between', className)} {...props}>
                                                                            <div className={cn('absolute top-1/2 -left-14')}>
                                                                                {prevWithClass}
                                                                            </div>
                                                                            <div></div>
                                                                        </nav>
                                                                    );
                                                                }
                                                            }}

                                                        />
                                                    </div>
                                                    <div className='lg:block hidden'>
                                                        <ReactDayPickerRange
                                                            mode='range'
                                                            className='max-w-full'
                                                            showOutsideDays={false}
                                                            fixedWeeks
                                                            selected={field.value as any}
                                                            onSelect={(date) => {
                                                                handleSelectDate(date);
                                                            }}
                                                            month={toMonth}
                                                            onMonthChange={handleNextMonth}
                                                            modifiers={{
                                                                booked: (date: Date) => generateModifiers(date) === 'booked',
                                                                blocked: (date: Date) => generateModifiers(date) === 'blocked'
                                                            }}
                                                            modifiersClassNames={{
                                                                booked: 'day-booked',
                                                                blocked: 'day-blocked'
                                                            }}
                                                            disabled={disableDates}
                                                            captionLayout='label'
                                                            components={{
                                                                Nav: ({ children, className, ...props }: NavProps) => {
                                                                    const [, nextButton] = React.Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;
                                                                    const nextWithClass = cloneElement(nextButton, {
                                                                        disabled: isAfter(toMonth, limitMonth) || (toMonth.getFullYear() === limitMonth.getFullYear() && toMonth.getMonth() >= limitMonth.getMonth())
                                                                    });
                                                                    return (
                                                                        <nav className={cn('flex items-center min-h-8 justify-between', className)} {...props}>
                                                                            <div></div>
                                                                            <div className='absolute top-1/2 -right-14'>{nextWithClass}</div>
                                                                        </nav>
                                                                    );
                                                                }
                                                            }}

                                                        />
                                                    </div>
                                                    <div className='lg:hidden'>
                                                        <ReactDayPickerRange
                                                            mode='range'
                                                            className='max-w-full w-full'
                                                            showOutsideDays={false}
                                                            fixedWeeks
                                                            selected={field.value as any}
                                                            onSelect={(date) => {
                                                                handleSelectDate(date);
                                                            }}
                                                            month={fromMonth}
                                                            onMonthChange={setFromMonth}
                                                            modifiers={{
                                                                booked: (date: Date) => generateModifiers(date) === 'booked',
                                                                blocked: (date: Date) => generateModifiers(date) === 'blocked'
                                                            }}
                                                            modifiersClassNames={{
                                                                booked: 'day-booked',
                                                                blocked: 'day-blocked'
                                                            }}
                                                            components={{
                                                                Nav: ({ children, className, ...props }: NavProps) => {
                                                                    const [previousButton, nextButton] = React.Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;
                                                                    const prevWithClass = React.cloneElement(previousButton, {
                                                                        disabled: fromMonth.getMonth() <= earliestMonth && fromMonth.getFullYear() === (new Date()).getFullYear()
                                                                        // nếu cần override hoàn toàn className, thay bằng:
                                                                        // className: 'my-prev-class'
                                                                    });
                                                                    const nextWithClass = cloneElement(nextButton, {
                                                                        disabled: isAfter(fromMonth, limitMonth) || (fromMonth.getFullYear() === limitMonth.getFullYear() && fromMonth.getMonth() >= limitMonth.getMonth())
                                                                    });

                                                                    return (
                                                                        <nav className={cn('flex items-center min-h-8 justify-between', className)} {...props}>
                                                                            {prevWithClass}
                                                                            {nextWithClass}
                                                                        </nav>
                                                                    );
                                                                }
                                                            }}
                                                            disabled={disableDates}
                                                            captionLayout='label'
                                                        />
                                                    </div>
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='col-span-full mt-5 flex flex-col gap-0.5 justify-center items-center text-[12px] leading-[20px]'>
                                <p className='item-des-blocked'>
                                    バイクのご利用が不可の日（点検など）
                                </p>
                                <p className='item-des-booked'>既に他の方が予約済みの日</p>
                            </div>

                            <div className='col-span-full xsm:mt-10 mt-5'>
                                <div className='flex xsm:flex-row flex-col justify-center items-center w-full xsm:gap-5 gap-2.5'>
                                    <FormField
                                        control={form.control}
                                        name='startTime'
                                        render={({ field }) => (
                                            <FormItem className='sm:w-[270px] xsm:w-[45%] w-full'>
                                                <div className='relative'>
                                                    {/* <FormLabel className='mb-0'>{t('booking_bike.field.rental_time')}</FormLabel> */}
                                                    <FormControl>
                                                        <CustomTimePickerVer2
                                                            className=''
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            placeholder='貸出時間を選択'
                                                            disableChangeMinute={true}
                                                        />
                                                    </FormControl>
                                                    <FormMessage className='absolute top-[45px] left-0' />
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                    <div className='h-full flex items-center'>
                                        <XCusIcon />
                                    </div>
                                    <FormField
                                        control={form.control}
                                        name='endTime'
                                        render={({ field }) => (
                                            <FormItem className='sm:w-[270px] xsm:w-[45%] w-full'>
                                                <div className='relative'>
                                                    {/* <FormLabel className='mb-0'>{t('booking_bike.field.return_time')}</FormLabel> */}
                                                    <FormControl>
                                                        <CustomTimePickerVer2
                                                            className=''
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            placeholder='返却時間を選択'
                                                            disableChangeMinute={true}
                                                        />
                                                    </FormControl>
                                                    <FormMessage className='absolute top-[45px] left-0' />
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className='md:mt-[80px] xsm:mt-[60px] mt-[30px] bg-subtle xsm:p-10 px-5 py-[30px] flex flex-col gap-2.5 rounded-[5px]'>
                                <div className='flex xsm:justify-start justify-between gap-5 font-medium'>
                                    <div className='xsm:w-[200px] point-375:w-[140px] w-[120px]'>{t('booking_bike.field.rental_date_time')}</div>
                                    <div className='flex-1 text-left'>{watchSelectedDates && watchStartTime ? formatDisplayDateTime(watchSelectedDates?.from, watchStartTime, DISPPLAY_MOMENT_YMD_HM_FULLWIDTH) : '--'}</div>
                                </div>
                                <div className='flex xsm:justify-start justify-between gap-5 font-medium'>
                                    <div className='xsm:w-[200px] point-375:w-[140px] w-[120px]'>{t('booking_bike.field.return_date_time')}</div>
                                    <div className='flex-1 text-left'>{watchSelectedDates && watchEndTime ? formatDisplayDateTime(watchSelectedDates?.to, watchEndTime, DISPPLAY_MOMENT_YMD_HM_FULLWIDTH) : '--'}</div>
                                </div>
                                <div className='flex xsm:justify-start justify-between gap-5 font-bold'>
                                    <div className='xsm:w-[200px] point-375:w-[140px] w-[120px]'>{t('booking_bike.field.total_rental_time')}</div>
                                    <div className='flex-1 text-left text-customRed'>{totalTime || '--'}</div>
                                </div>
                                <div className='h-[1px] w-full bg-border-gray'></div>
                                <div className='flex xsm:justify-start justify-between gap-5 font-bold'>
                                    <div className='xsm:w-[200px] point-375:w-[140px] w-[120px]'>{`${t('booking_bike.field.total_amount')}: `}</div>
                                    <span className='text-customRed font-semibold flex-1 text-left'>
                                        {formatCurrency(totalAmount)}
                                        {t('currency.currency_unit')}
                                    </span>
                                </div>
                            </div>

                            <div className='md:mt-[80px] xsm:mt-[60px] mt-[30px] flex justify-center items-center'>
                                <AnimateButton
                                    parentClassName='xsm:w-[420px] w-full h-[67px]'
                                    type='submit'
                                    contentComp={t('button.next')}
                                >
                                </AnimateButton>
                            </div>
                        </div>
                    </div>
                </StepWrapper>

            </form>
        </Form>
    );
});
